import { MultiDatePicker } from 'components/MyDatePicker/MultiDatePicker';
import React, { Component, Fragment } from 'react';
import { Formik, Form, Field, FormikProps, FieldProps } from 'formik';
import { NumberOrNull } from 'store/entity/types';
import messages from './Messages';
import Icon from 'components/Icon';
import Input from 'components/Input';
import LoadingIcon from 'components/LoadingIcon';
import { Wrapper, InputsWrapper, Action } from './styles';
import { removeDuplicates } from 'utils/happyFunctions';
import { isANumber } from 'utils/number';
import {
  ICheckPoint,
  IMultipleChoiceQuestionView,
  IQuestionsView,
  ISurveyView,
} from 'store/survey/surveyTypes';
import { ITags } from 'store/providers/providerTypes';
import i18n from '../../i18n';

interface IComponentProps {
  canUpdate: boolean;
  checkPoint?: ICheckPoint;
  onCancel?: () => void;
  onRemove: (checkPointId: number) => void;
  onSave: (checkPoint: ICheckPoint, isUpdate: boolean) => Promise<void>;
  reportId?: number;
  surveyId?: number;
  survey?: ISurveyView;
  tags: ITags['data'];
}

interface IComponentStates {
  editing: boolean;
  loading: boolean;
}

interface IFormValues {
  baseLine: NumberOrNull;
  date: NumberOrNull;
  target: NumberOrNull;
  splitPerDistinctive: boolean;
  properties: {
    distinctiveValues: {
      [key: string]: { baseLine?: number; target?: number };
    };
  };
}

class CheckPointEditor extends Component<IComponentProps, IComponentStates> {
  state: IComponentStates = {
    editing: !this.props.checkPoint,
    loading: false,
  };

  handleChangeEditing = () => {
    this.setState((prevState) => ({ editing: !prevState.editing }));
  };

  handleCancelEditing = () => {
    if (!this.props.checkPoint && this.props.onCancel) {
      // creating
      this.props.onCancel();
    } else {
      // editing
      this.handleChangeEditing();
    }
  };

  onSubmitForm = (values: IFormValues) => {
    if (values.date) {
      this.setState({ loading: true });
      const checkPoint: ICheckPoint = {
        id: this.props.checkPoint ? this.props.checkPoint.id : undefined,
        baseLine: Number(values.baseLine),
        target: Number(values.target),
        date: Number(values.date),
        surveyId: this.props.surveyId ? this.props.surveyId : null,
        reportId: this.props.reportId ? this.props.reportId : null,
        properties: {
          distinctiveValues: Object.entries(
            values.properties?.distinctiveValues || {}
          ).reduce((prev, item) => {
            if (isANumber(item[1].baseLine) && isANumber(item[1].target))
              prev[item[0].substring(1)] = item[1];
            return prev;
          }, {}),
        },
      };
      if (this.props.checkPoint) {
        // updating
        this.props.onSave(checkPoint, true).then(() => {
          this.setState({ editing: false, loading: false });
        });
      } else {
        // creating
        this.props.onSave(checkPoint, false).then(() => {
          this.setState({ loading: false });
        });
      }
    }
  };

  handleRemove = () => {
    if (
      this.props.checkPoint &&
      confirm(i18n.t('areYouSureToRemoveTheCheckPoint', { ns: 'survey' }))
    ) {
      this.props.onRemove(this.props.checkPoint.id!);
    }
  };

  handleInputChanges = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
  };

  render() {
    const { checkPoint, survey, tags } = this.props;

    const distinctiveQuestion = survey?.questionMap
      ? Object.values(survey.questionMap).reduce(
          (firstDistinctive: IQuestionsView | undefined, question) => {
            if (
              !question.data.deleted &&
              question.data.distinctive &&
              (!firstDistinctive ||
                question.data.order < firstDistinctive.order)
            )
              return question.data;
            return firstDistinctive;
          },
          undefined
        )
      : [];
    let choices: IMultipleChoiceQuestionView['choiceList'] | Array<number> = [];
    const question: IMultipleChoiceQuestionView | undefined =
      distinctiveQuestion &&
      (distinctiveQuestion as IMultipleChoiceQuestionView);
    if (question?.useTags && tags) {
      // todo: parent tags could be null instead of array
      if (question.parentTagChoices) {
        question.parentTagChoices.forEach((parentId: number) => {
          //todo should check for null
          Object.values(tags)
            .filter(
              (tag) =>
                tag.parentId === parentId &&
                !tag.deleted &&
                !question!.excludedTagChoices?.includes(Number(tag.id))
            )
            .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
            // @ts-expect-error it's changing type
            .map((item) => choices.push(item.id));
        });
      }
      if (question.tagChoices) {
        question.tagChoices.forEach((tagChoice: number) => {
          choices.push(tagChoice as any);
        });
      }
      // @ts-expect-error it's changing type
      choices = removeDuplicates(choices).map((tagId: number) => ({
        value: tags[tagId].id.toString(),
        text: tags[tagId].text,
        score: 0,
      }));
    } else {
      choices = question?.choiceList || [];
    }

    const initialValues: IFormValues = {
      baseLine: checkPoint ? checkPoint.baseLine : null,
      date: checkPoint?.date || null,
      target: checkPoint ? checkPoint.target : null,
      splitPerDistinctive: !!(
        question &&
        Object.values(checkPoint?.properties?.distinctiveValues || {}).length >
          0
      ),
      properties: {
        // @ts-expect-error it's ok
        distinctiveValues:
          question &&
          Object.values(checkPoint?.properties?.distinctiveValues || {}).reduce(
            (current, item, key) => {
              current['c' + key] = item;
              return current;
            },
            {}
          ),
      },
    };

    return (
      <div>
        <Formik
          onSubmit={this.onSubmitForm}
          initialValues={initialValues}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {(formikBag: FormikProps<IFormValues>) => (
            // @ts-expect-error JSX error
            <Form>
              <Wrapper>
                <InputsWrapper>
                  <Field
                    name="date"
                    render={({ field }: FieldProps) => (
                      <MultiDatePicker
                        setDate={(value) => {
                          formikBag.setFieldValue('date', value);
                        }}
                        id={`${checkPoint?.id || '_'}date`}
                        value={field.value ? field.value : null}
                        label={
                          i18n.t('date', {
                            ns: 'survey',
                          }) as string
                        }
                        placeholder={
                          i18n.t('date', {
                            ns: 'survey',
                          }) as string
                        }
                        isEditable={
                          !(!this.state.editing && !!checkPoint) &&
                          !this.state.loading
                        }
                      />
                    )}
                  />

                  <Field
                    name="baseLine"
                    render={({ field }: FieldProps) => (
                      <Input
                        {...field}
                        type="number"
                        id={`${checkPoint?.id || '_'}baseLine`}
                        required={true}
                        label={
                          i18n.t('baseLine', {
                            ns: 'survey',
                          }) as string
                        }
                        placeholder={
                          i18n.t('baseLine', {
                            ns: 'survey',
                          }) as string
                        }
                        disabled={
                          (!this.state.editing && !!checkPoint) ||
                          this.state.loading
                        }
                      />
                    )}
                  />
                  <Field
                    name="target"
                    render={({ field }: FieldProps) => (
                      <Input
                        {...field}
                        type="number"
                        required={true}
                        id={`${checkPoint?.id || '_'}target`}
                        label={
                          i18n.t('target', {
                            ns: 'survey',
                          }) as string
                        }
                        placeholder={
                          i18n.t('target', {
                            ns: 'survey',
                          }) as string
                        }
                        disabled={
                          (!this.state.editing && !!checkPoint) ||
                          this.state.loading
                        }
                      />
                    )}
                  />
                  {question && (
                    <Field
                      name="splitPerDistinctive"
                      render={({ field }: FieldProps) => (
                        <Input
                          {...field}
                          type="checkbox"
                          checked={field.value}
                          id={`${checkPoint?.id || '_'}splitPerDistinctive`}
                          label={messages.splitPerDistinctive(question?.title)}
                          disabled={
                            (!this.state.editing && !!checkPoint) ||
                            this.state.loading
                          }
                        />
                      )}
                    />
                  )}
                </InputsWrapper>
                {distinctiveQuestion &&
                  formikBag.values.splitPerDistinctive &&
                  // @ts-expect-error it's changing type
                  choices.map((choice: IChoiceView) => {
                    return (
                      <InputsWrapper key={choice.value}>
                        <div>{choice.text}</div>

                        <Field
                          name={`properties.distinctiveValues.c${choice.value}.baseLine`}
                          render={({ field }: FieldProps) => (
                            <Input
                              {...field}
                              type="number"
                              id={`${checkPoint?.id || '_'}${
                                choice.value
                              }baseLine`}
                              required={true}
                              label={
                                i18n.t('baseLine', {
                                  ns: 'survey',
                                }) as string
                              }
                              placeholder={
                                i18n.t('baseLine', {
                                  ns: 'survey',
                                }) as string
                              }
                              disabled={
                                (!this.state.editing && !!checkPoint) ||
                                this.state.loading
                              }
                            />
                          )}
                        />

                        <Field
                          name={`properties.distinctiveValues.c${choice.value}.target`}
                          render={({ field }: FieldProps) => (
                            <Input
                              {...field}
                              type="number"
                              required={true}
                              id={`${checkPoint?.id || '_'}${
                                choice.value
                              }target`}
                              label={
                                i18n.t('target', { ns: 'survey' }) as string
                              }
                              placeholder={
                                i18n.t('target', { ns: 'survey' }) as string
                              }
                              disabled={
                                (!this.state.editing && !!checkPoint) ||
                                this.state.loading
                              }
                            />
                          )}
                        />
                        <div />
                      </InputsWrapper>
                    );
                  })}
                {this.props.canUpdate && (
                  <div
                    style={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'flex-end',
                    }}
                  >
                    {this.state.loading ? (
                      <LoadingIcon
                        customStyle={{
                          margin: '0 0.5em',
                          position: 'relative',
                        }}
                      />
                    ) : this.state.editing ? (
                      <Fragment>
                        <Action
                          color="green"
                          onClick={() => formikBag.handleSubmit()}
                        >
                          <Icon type="save" />
                        </Action>
                        <Action color="red" onClick={this.handleCancelEditing}>
                          <Icon type="x" />
                        </Action>
                      </Fragment>
                    ) : (
                      <Fragment>
                        <Action color="blue" onClick={this.handleChangeEditing}>
                          <Icon type="edit-2" />
                        </Action>
                        <Action color="red" onClick={this.handleRemove}>
                          <Icon type="trash-2" />
                        </Action>
                      </Fragment>
                    )}
                  </div>
                )}
              </Wrapper>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}

export default CheckPointEditor;
